// The Action Center (BRD 4.1; DC-44 … DC-47; V1-INTERACTIONS §4.27): "Prioritized actions across reqs, candidates,
// scheduling, packets, scorecards, panels, approvals, offers and closeout. Deep link to exact screen; close
// automatically when resolved; retain history." One read, `action_center.list`; the server decides which items are the
// person's (their own, the role pools they hold within scope, the cards a delegation covers) and builds every row's
// text. This page only arranges them.
//
// Routes: #action-center (Prioritized actions) and #action-center/history (History) are route tabs; the query carries
// view state only — ?kind=<k>[,<k>] · tier=<t>[,<t>] · due=overdue|today|week · sort=priority|due|requisition ·
// req=R-#### — so the Approvals nav item is #action-center?kind=approvals and Home's breach alert is ?tier=breach.
// Priority order groups the rows by SLA tier (Breach — dashboard risk · Near-breach · Reminder · Due · Blocked); the
// requisition sort groups them under one heading per requisition, its title and id (a request-stage row under its
// request; the rows on neither last, F-78); the due sort is one list. There is no dismiss control anywhere (DC-46): a
// row leaves Prioritized actions only when
// its act happens, and a row that was here at the last read and is gone at this one collapses in 200 ms (DC-47),
// then appears in History as Completed, by whom, when.
//
// D-18 (comparison AC-10, AC-19, AC-24, AC-27, AC-30): the view of approvals alone is the screen his navigation names
// Approvals (DC-214), so it is titled Approvals; the tab counts follow the active filter (the server's open_filtered,
// and History under the same filters); the priority sort reads his Priority. History reads as history: its kind chips
// count the closed rows, its rows do not lift, a row leads to the timeline that records its act (the requisition's
// Requisition timeline, else the Hiring Request with its History; a row on neither keeps its own screen), and an empty
// History reads his BRD 4.1 line for what it keeps instead of the open tab's *Nothing needs action* (Q-D3). Each tier
// group, and every list, sits on a Card. At ≤ 768 px one Filters button opens the slide-over with the ten chips and only
// the active chips stay inline (40-g3-metrics.css swaps the two rows; each place stamps its own hotspot keys).

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { actionCenterList, type ActionCenterListInput, type ActionRow as ActionItemRow } from "@/api/tools";
import { errorText, useRead } from "@/api/useTool";
import {
  ActionRows, Button, Card, Chip, Chips, CORE_LABELS, Count, ErrorStrip, Flex, FILTERBAR_LABELS, METRIC_LABELS, NAV_ITEM_LABELS,
  PageHeader, SectionTitle, Segmented, SlideOver, SLA_PILL_LABELS, SLA_TIER_LABELS, Stack, Tabs, type Hs,
} from "@/mc";
import type { Route } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { ACTION_KINDS, ACTION_TIERS, ActionCenterRow, type ActionTier } from "@/views/actions/ActionRow";
import { ACTION_CENTER_LABELS, ACTION_KIND_CHIP_LABELS } from "@/views/actions/labels";
import { McPage, isCapturePage, settled, useCaptureSettle } from "@/shell/page";

type Tab = "open" | "history";
type Kind = (typeof ACTION_KINDS)[number];
type Due = "overdue" | "today" | "week";
type Sort = "priority" | "due" | "requisition";

const DUES: readonly Due[] = ["overdue", "today", "week"];
const SORTS: readonly Sort[] = ["priority", "due", "requisition"];
const POLL_MS = 60_000;
/** One page holds a person's actions (the server's limit is 200); History is newest first. */
const LIMIT = 200;

/** His word for each tier group (DC-45, DC-19); Blocked is DS §1.4's. */
const TIER_GROUP: Readonly<Record<ActionTier, string>> = {
  breach: SLA_TIER_LABELS.breach,
  near_breach: SLA_TIER_LABELS.nearBreach,
  reminder: SLA_TIER_LABELS.reminder,
  due: SLA_TIER_LABELS.due,
  blocked: ACTION_CENTER_LABELS.blocked,
};

export interface ActionCenterView {
  tab: Tab;
  kinds: Kind[];
  tiers: ActionTier[];
  due: Due | null;
  sort: Sort;
  req: string | null;
}

const list = <T extends string>(raw: string | null, allowed: readonly T[]): T[] =>
  (raw ?? "").split(",").map((part) => part.trim()).filter((part): part is T => (allowed as readonly string[]).includes(part));

/** The view state from the route: the tab from the first part, the rest from the query; unknown values are dropped. */
export function viewOf(route: Pick<Route, "parts" | "raw">): ActionCenterView {
  const query = new URLSearchParams(route.raw.includes("?") ? route.raw.slice(route.raw.indexOf("?") + 1) : "");
  const due = query.get("due");
  const sort = query.get("sort");
  const req = query.get("req");
  return {
    tab: route.parts[0] === "history" ? "history" : "open",
    kinds: list(query.get("kind"), ACTION_KINDS),
    tiers: list(query.get("tier"), ACTION_TIERS),
    due: due && (DUES as readonly string[]).includes(due) ? (due as Due) : null,
    sort: sort && (SORTS as readonly string[]).includes(sort) ? (sort as Sort) : "priority",
    req: req && /^R-\d+$/.test(req) ? req : null,
  };
}

/** The hash for a view state (the default sort and empty filters are left out). */
export function hashOf(view: ActionCenterView): string {
  const query = new URLSearchParams();
  if (view.kinds.length) query.set("kind", view.kinds.join(","));
  if (view.tiers.length) query.set("tier", view.tiers.join(","));
  if (view.due) query.set("due", view.due);
  if (view.sort !== "priority") query.set("sort", view.sort);
  if (view.req) query.set("req", view.req);
  const qs = query.toString().replace(/%2C/g, ",");
  return `#action-center${view.tab === "history" ? "/history" : ""}${qs ? `?${qs}` : ""}`;
}

/** The read's cache key: one entry per tab and view state. */
export function readKey(view: ActionCenterView): readonly string[] {
  return ["action-center", view.tab, view.kinds.join(","), view.tiers.join(","), view.due ?? "", view.sort, view.req ?? ""];
}

/** Approvals (DC-214): the Action Center filtered to approvals alone is the screen his navigation names Approvals. */
export function titleOf(view: Pick<ActionCenterView, "kinds">): string {
  return view.kinds.length === 1 && view.kinds[0] === "approvals" ? NAV_ITEM_LABELS.approvals : METRIC_LABELS.actionCenter;
}

type Counts = Readonly<Record<string, number>>;

/** The tab counts under the active filter: the open rows the kind and tier filters keep (open_filtered; the plain open
 * count from a server that sends none), and the closed rows History keeps under the same filters. */
export function tabCounts(counts: Counts): { open: number | undefined; history: number | undefined } {
  return { open: counts.open_filtered ?? counts.open, history: counts.history };
}

/** A kind chip's count: its open rows on Prioritized actions, its closed rows on History. */
export function kindCount(counts: Counts, tab: Tab, kind: Kind): number {
  return (tab === "history" ? counts[`history_kind:${kind}`] : counts[`kind:${kind}`]) ?? 0;
}

type RowRoute = Pick<ActionItemRow, "route" | "requisition_id" | "requisition_title" | "request_id" | "request_title">;

/**
 * Where a History row leads: the timeline that records its act. A row on a requisition the reader may open leads to its
 * Requisition timeline (for a seat that reads it); a request-stage row to the Hiring Request, whose History records it;
 * any other row keeps its own screen.
 */
export function historyRoute(row: RowRoute, timeline: boolean): string {
  const own = row.route ?? "#action-center";
  if (row.requisition_id) return timeline && row.requisition_title != null ? `#requisitions/${row.requisition_id}/timeline` : own;
  if (row.request_id && row.request_title != null) return `#requests/${row.request_id}`;
  return own;
}

type Go = (next: Partial<ActionCenterView>) => void;
type GroupRow = Pick<ActionItemRow, "requisition_id" | "requisition_title" | "request_id" | "request_title" | "request_detail">;

/** One group of the requisition sort: its record's id and title (empty where the reader may not open it), or none. */
export interface RecordGroup<T> {
  key: string | null;
  title: string;
  members: T[];
}

/**
 * The requisition sort's groups (F-78), in the server's order: one per requisition, named by its title (where the
 * reader may open it) and its id; a request-stage row's request heads its own group the same way; the rows on neither
 * close the list as one group with no record.
 */
export function requisitionGroups<T extends { row: GroupRow }>(items: readonly T[]): RecordGroup<T>[] {
  const groups = new Map<string | null, RecordGroup<T>>();
  for (const item of items) {
    const { row } = item;
    const key = row.requisition_id ?? row.request_id ?? null;
    let group = groups.get(key);
    if (!group) {
      const title = row.requisition_id ? row.requisition_title : row.request_id ? row.request_title ?? row.request_detail : null;
      group = { key, title: title ?? "", members: [] };
      groups.set(key, group);
    }
    group.members.push(item);
  }
  const rest = groups.get(null);
  groups.delete(null);
  return rest ? [...groups.values(), rest] : [...groups.values()];
}

/** Where a chip is drawn: the full row, the phone's inline row of active chips, or the sheet. */
type Place = "row" | "phone" | "sheet";

/** One page never stamps a hotspot key twice: each place keys its chips under its own head. */
function kindHs(place: Place, kind: Kind): Hs {
  return place === "row" ? `ac.chip.${kind}` : place === "phone" ? `ac.on.${kind}` : `ac.sheet.${kind}`;
}
const OVERDUE_HS: Readonly<Record<Place, Hs>> = { row: "ac.due.overdue", phone: "ac.on.overdue", sheet: "ac.sheet.overdue" };

/** The ten toggles (the nine kinds with their counts on this tab, then Overdue); `active` keeps only the chosen ones. */
function toggles(place: Place, view: ActionCenterView, counts: Counts, go: Go, active = false): ReactNode[] {
  const kinds = active ? ACTION_KINDS.filter((kind) => view.kinds.includes(kind)) : ACTION_KINDS;
  const chips = kinds.map((kind) => (
    <Chip
      key={kind}
      sm
      on={view.kinds.includes(kind)}
      onToggle={(on) => go({ kinds: on ? ACTION_KINDS.filter((k) => k === kind || view.kinds.includes(k)) : view.kinds.filter((k) => k !== kind) })}
      hs={kindHs(place, kind)}
    >
      {ACTION_KIND_CHIP_LABELS[kind]} <Count n={kindCount(counts, view.tab, kind)} />
    </Chip>
  ));
  if (!active || view.due === "overdue") {
    chips.push(
      <Chip key="overdue" sm on={view.due === "overdue"} onToggle={(on) => go({ due: on ? "overdue" : null })} hs={OVERDUE_HS[place]}>
        {SLA_PILL_LABELS.overdue}
      </Chip>,
    );
  }
  return chips;
}

/** The chosen filters with no toggle of their own (a due window, tiers, a requisition), each removable. */
function removable(place: Exclude<Place, "sheet">, view: ActionCenterView, go: Go): ReactNode[] {
  const row = place === "row";
  return [
    view.due && view.due !== "overdue" ? (
      <Chip key="due" sm onRemove={() => go({ due: null })} hs={row ? "ac.filter.due" : "ac.on.due"}>{SLA_TIER_LABELS.due}</Chip>
    ) : null,
    view.tiers.length ? (
      <Chip key="tier" sm onRemove={() => go({ tiers: [] })} hs={row ? "ac.filter.tier" : "ac.on.tier"}>
        {view.tiers.map((tier) => TIER_GROUP[tier]).join(" · ")}
      </Chip>
    ) : null,
    view.req ? (
      <Chip key="req" sm name={ACTION_CENTER_LABELS.requisition} onRemove={() => go({ req: null })} hs={row ? "ac.filter.req" : "ac.on.req"}>
        <span className="mc-mono">{view.req}</span>
      </Chip>
    ) : null,
  ].filter(Boolean);
}

export interface FiltersSheetProps {
  open: boolean;
  onClose: () => void;
  view: ActionCenterView;
  counts: Counts;
  go: Go;
}

/** At ≤ 768 px: the design system's slide-over (the full width there) holding the ten toggles. */
export function FiltersSheet({ open, onClose, view, counts, go }: FiltersSheetProps) {
  return (
    <SlideOver open={open} onClose={onClose} title={ACTION_CENTER_LABELS.filters} >
      <Chips>{toggles("sheet", view, counts, go)}</Chips>
    </SlideOver>
  );
}

function inputOf(view: ActionCenterView): ActionCenterListInput {
  return {
    tab: view.tab,
    kinds: view.kinds.length ? view.kinds : null,
    tiers: view.tiers.length ? view.tiers : null,
    due: view.due,
    requisition_id: view.req,
    sort: view.sort,
    limit: LIMIT,
  };
}

/**
 * Auto-close (DC-47): a row present at the previous read of the same view and absent from this one stays drawn with
 * `closing` at its old place until its 200 ms collapse ends. A changed view (another tab, filter or sort) starts over.
 */
export function useAutoClose(rows: readonly ActionItemRow[] | undefined, viewKey: string) {
  const previous = useRef<{ key: string; rows: readonly ActionItemRow[] } | null>(null);
  const [closing, setClosing] = useState<{ key: string; rows: { row: ActionItemRow; at: number }[] }>({ key: viewKey, rows: [] });
  useEffect(() => {
    if (!rows) return;
    const before = previous.current;
    previous.current = { key: viewKey, rows };
    if (!before || before.key !== viewKey) {
      setClosing({ key: viewKey, rows: [] });
      return;
    }
    const present = new Set(rows.map((row) => row.id));
    const gone = before.rows
      .map((row, at) => ({ row, at }))
      .filter(({ row }) => !present.has(row.id));
    if (!gone.length) return;
    setClosing((current) => {
      const kept = current.key === viewKey ? current.rows.filter(({ row }) => !present.has(row.id)) : [];
      return { key: viewKey, rows: [...kept, ...gone.filter(({ row }) => !kept.some((k) => k.row.id === row.id))] };
    });
  }, [rows, viewKey]);
  const done = useCallback((id: string) => {
    setClosing((current) => ({ ...current, rows: current.rows.filter(({ row }) => row.id !== id) }));
  }, []);
  const merged = useMemo(() => {
    const out: { row: ActionItemRow; closing: boolean }[] = (rows ?? []).map((row) => ({ row, closing: false }));
    if (closing.key !== viewKey) return out;
    for (const { row, at } of [...closing.rows].sort((a, b) => a.at - b.at)) out.splice(Math.min(at, out.length), 0, { row, closing: true });
    return out;
  }, [rows, closing, viewKey]);
  return { rows: merged, done };
}

function ActionCenterScreen({ route }: { route: Pick<Route, "parts" | "raw"> }) {
  const view = viewOf(route);
  const viewKey = hashOf(view);
  const { register } = useViewState();
  const { can } = useSession();
  const settle = useCaptureSettle();
  const [sheet, setSheet] = useState(false);
  const q = useRead(
    readKey(view),
    () => settled(settle, actionCenterList(inputOf(view))),
    { refetchInterval: isCapturePage() ? false : POLL_MS },
  );
  const counts = q.data?.counts ?? {};
  const tabs = tabCounts(counts);
  const history = view.tab === "history";
  const title = titleOf(view);
  const timeline = can("timeline.requisition");
  const { rows, done } = useAutoClose(history ? undefined : q.data?.items, viewKey);
  const shown = history
    ? (q.data?.items ?? []).map((row) => ({ row: { ...row, route: historyRoute(row, timeline) }, closing: false }))
    : rows;

  useEffect(() => {
    register({
      view: "action-center",
      ids: { tab: view.tab, ...(view.req ? { requisition: view.req } : {}) },
      summary: q.data ? { tab: view.tab, loading: false, count: q.data.items.length } : { tab: view.tab, loading: q.isLoading },
    });
  }, [register, view.tab, view.req, q.data, q.isLoading]);

  const go: Go = (next) => {
    window.location.hash = hashOf({ ...view, ...next });
  };
  const inline = [...toggles("phone", view, counts, go, true), ...removable("phone", view, go)];

  // Row hotspots number the drawn rows in order, groups included (ac.row.0 is the first row on the screen).
  let index = 0;
  const rowOf = (item: { row: ActionItemRow; closing: boolean }) => {
    const hs = `ac.row.${index++}`;
    return (
      <ActionCenterRow
        key={item.row.id}
        row={item.row}
        history={history}
        closing={item.closing}
        onClosed={item.closing ? () => done(item.row.id) : undefined}
        hs={hs}
      />
    );
  };
  const grouped = !history && view.sort === "priority";
  const byRequisition = !history && view.sort === "requisition";

  return (
    <Stack gap={16}>
      <PageHeader title={title} />
      <Tabs
        route
        label={title}
        active={view.tab}
        items={[
          { key: "open", label: METRIC_LABELS.prioritizedActions, count: tabs.open, href: hashOf({ ...view, tab: "open" }), hs: "ac.tab.open" },
          { key: "history", label: METRIC_LABELS.history, count: tabs.history, href: hashOf({ ...view, tab: "history" }), hs: "ac.tab.history" },
        ]}
      />
      <Flex wrap justify="between" align="center" gap={12}>
        <Chips className="mc-ac-chips">
          {toggles("row", view, counts, go)}
          {removable("row", view, go)}
        </Chips>
        <div className="mc-ac-phone">
          {/* opens a modal slide-over that takes focus: aria-haspopup="dialog" names it; no aria-expanded, whose aria-controls
              target would not exist while the sheet is closed (it mounts only when open) */}
          <Button sm aria-haspopup="dialog" onClick={() => setSheet(true)} >
            {ACTION_CENTER_LABELS.filters}
          </Button>
          {inline.length ? <Chips>{inline}</Chips> : null}
        </div>
        {history ? null : (
          <Segmented<Sort>
            label={FILTERBAR_LABELS.sort}
            value={view.sort}
            onChange={(sort) => go({ sort })}
            options={[
              { value: "priority", label: ACTION_CENTER_LABELS.priority, hs: "ac.sort.priority" },
              { value: "due", label: SLA_TIER_LABELS.due, hs: "ac.sort.due" },
              { value: "requisition", label: ACTION_CENTER_LABELS.requisition, hs: "ac.sort.requisition" },
            ]}
          />
        )}
      </Flex>
      <FiltersSheet open={sheet} onClose={() => setSheet(false)} view={view} counts={counts} go={go} />
      {q.isError ? (
        <ErrorStrip message={errorText(q.error)} onRetry={() => void q.refetch()} retrying={q.isFetching} live  />
      ) : null}
      {q.isLoading ? <Card tight><ActionRows loading={4} /></Card> : null}
      {q.data && grouped && shown.length ? (
        ACTION_TIERS.map((tier) => {
          const members = shown.filter((item) => item.row.tier === tier);
          if (!members.length) return null;
          const id = `ac-tier-${tier}`;
          return (
            <Card key={tier} tight>
              <section aria-labelledby={id}>
                <SectionTitle as="h2" id={id} count={members.filter((item) => !item.closing).length}>{TIER_GROUP[tier]}</SectionTitle>
                {/* F-180 ("Lets see multiple per req also"): inside the tier, the rows of one requisition (or request) sit
                    under its heading — title · id · count — the groups in priority order (each at its first row), the rows
                    on no record last, as they are */}
                {requisitionGroups(members).map((group) => {
                  if (group.key === null) return <ActionRows key="none" labelledBy={id}>{group.members.map(rowOf)}</ActionRows>;
                  const sub = `${id}-${group.key}`;
                  return (
                    <section key={group.key} aria-labelledby={sub} className="mc-ac-req">
                      <SectionTitle as="h3" id={sub} count={group.members.filter((item) => !item.closing).length}>
                        {group.title}{group.title ? " · " : null}<span className="mc-mono">{group.key}</span>
                      </SectionTitle>
                      <ActionRows labelledBy={sub}>{group.members.map(rowOf)}</ActionRows>
                    </section>
                  );
                })}
              </section>
            </Card>
          );
        })
      ) : q.data && byRequisition && shown.length ? (
        requisitionGroups(shown).map((group) => {
          if (group.key === null) return <Card key="ac-req-none" tight><ActionRows>{group.members.map(rowOf)}</ActionRows></Card>;
          const id = `ac-req-${group.key}`;
          return (
            <Card key={group.key} tight>
              <section aria-labelledby={id}>
                <SectionTitle as="h2" id={id} count={group.members.filter((item) => !item.closing).length}>
                  {group.title}{group.title ? " · " : null}<span className="mc-mono">{group.key}</span>
                </SectionTitle>
                <ActionRows labelledBy={id}>{group.members.map(rowOf)}</ActionRows>
              </section>
            </Card>
          );
        })
      ) : q.data ? (
        <Card tight><ActionRows empty={history ? CORE_LABELS.retainHistory : undefined}>{shown.map(rowOf)}</ActionRows></Card>
      ) : null}
    </Stack>
  );
}

/** #action-center and #action-center/history; the query is view state (see the header comment). */
export function ActionCenter({ route }: { route: Pick<Route, "parts" | "raw"> }) {
  return (
    <McPage>
      <ActionCenterScreen route={route} />
    </McPage>
  );
}

export default ActionCenter;
